import React from 'react'
import { Box, styled, keyframes } from '@mui/material'

export interface QuillIconProps {
  size?: number
  color?: string
  animated?: boolean
}

// Sparkle data with paths and transform origins
const sparkleData = [
  {
    d: 'M8.15918 149.457C8.05664 149.182 7.79297 149 7.5 149C7.20703 149 6.94336 149.182 6.84082 149.457L5.10352 154.104L0.457031 155.841C0.181641 155.943 0 156.207 0 156.5C0 156.793 0.181641 157.057 0.457031 157.159L5.10352 158.899L6.84375 163.546C6.94336 163.818 7.20703 164 7.5 164C7.79297 164 8.05664 163.818 8.15918 163.543L9.89941 158.896L14.5459 157.156C14.8184 157.057 15 156.793 15 156.5C15 156.207 14.8184 155.943 14.543 155.841L9.89648 154.104L8.15918 149.457Z',
    originX: 7.5,
    originY: 156.5,
  },
  {
    d: 'M44.335 171.579C44.2051 171.23 43.8711 171 43.5 171C43.1289 171 42.7949 171.23 42.665 171.579L40.4645 177.464L34.5789 179.665C34.2301 179.795 34 180.129 34 180.5C34 180.871 34.2301 181.205 34.5789 181.335L40.4645 183.539L42.6688 189.425C42.7949 189.77 43.1289 190 43.5 190C43.8711 190 44.2051 189.77 44.335 189.421L46.5393 183.536L52.4248 181.331C52.7699 181.205 53 180.871 53 180.5C53 180.129 52.7699 179.795 52.4211 179.665L46.5355 177.464L44.335 171.579Z',
    originX: 43.5,
    originY: 180.5,
  },
  {
    d: 'M102.352 107.244C102.297 107.097 102.156 107 102 107C101.844 107 101.703 107.097 101.648 107.244L100.722 109.722L98.2437 110.648C98.0969 110.703 98 110.844 98 111C98 111.156 98.0969 111.297 98.2437 111.352L100.722 112.28L101.65 114.758C101.703 114.903 101.844 115 102 115C102.156 115 102.297 114.903 102.352 114.756L103.28 112.278L105.758 111.35C105.903 111.297 106 111.156 106 111C106 110.844 105.903 110.703 105.756 110.648L103.278 109.722L102.352 107.244Z',
    originX: 102,
    originY: 111,
  },
  {
    d: 'M30.3516 71.2437C30.2969 71.0969 30.1563 71 30 71C29.8438 71 29.7031 71.0969 29.6484 71.2437L28.7219 73.7219L26.2437 74.6484C26.0969 74.7031 26 74.8438 26 75C26 75.1563 26.0969 75.2969 26.2437 75.3516L28.7219 76.2797L29.65 78.7578C29.7031 78.9031 29.8438 79 30 79C30.1563 79 30.2969 78.9031 30.3516 78.7563L31.2797 76.2781L33.7578 75.35C33.9031 75.2969 34 75.1563 34 75C34 74.8438 33.9031 74.7031 33.7563 74.6484L31.2781 73.7219L30.3516 71.2437Z',
    originX: 30,
    originY: 75,
  },
  {
    d: 'M98.1592 0.457031C98.0566 0.181641 97.793 0 97.5 0C97.207 0 96.9434 0.181641 96.8408 0.457031L95.1035 5.10352L90.457 6.84082C90.1816 6.94336 90 7.20703 90 7.5C90 7.79297 90.1816 8.05664 90.457 8.15918L95.1035 9.89941L96.8438 14.5459C96.9434 14.8184 97.207 15 97.5 15C97.793 15 98.0566 14.8184 98.1592 14.543L99.8994 9.89648L104.546 8.15625C104.818 8.05664 105 7.79297 105 7.5C105 7.20703 104.818 6.94336 104.543 6.84082L99.8965 5.10352L98.1592 0.457031Z',
    originX: 97.5,
    originY: 7.5,
  },
]

// Quill writing animation - 3 phase sequence (total 1800ms)
const quillWriting = keyframes`
  0% {
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
  22% { /* 400ms / 1800ms */
    transform: translate(5px, -5px) rotate(-10deg) scale(0.95);
  }
  55% { /* 1000ms / 1800ms */
    transform: translate(10px, 5px) rotate(10deg) scale(1.05);
  }
  100% {
    transform: translate(0, 0) rotate(0deg) scale(1);
  }
`

// Sparkle fade in/out animation (2500ms total)
const sparkleFade = keyframes`
  0%, 100% {
    opacity: 0;
    transform: scale(0);
  }
  20% {
    opacity: 1;
    transform: scale(1);
  }
  60% {
    opacity: 1;
    transform: scale(1);
  }
  80% {
    opacity: 0;
    transform: scale(0);
  }
`

const QUILL_PATH = 'M29.499 154.96C37.4958 127.437 74.4812 60.381 125.961 24.8513C90.7858 55.7366 68.979 81.8471 45.4926 128.438C29.9988 160.965 30.9984 162.967 30.9984 162.967C34.5421 160.614 37.9465 157.91 40.9944 156.461C50.4719 151.957 49.9908 154.46 58.9873 149.956C64.6868 147.102 65.5415 145.91 68.9833 144.451C66.7618 143.688 57.3107 144.844 47.9916 145.452C72.2658 136.718 82.9802 128.718 95.9727 107.921C83.9375 113.117 79.1857 113.674 72.9818 111.924C89.2864 107.228 96.6887 103.8 104.969 95.4102C118.464 79.8973 117.961 77.1872 124.961 63.3835C116.846 66.9577 112.245 68.2888 103.969 69.8889C126.519 58.6673 135.601 51.8858 144.454 38.863C140.162 40.877 137.434 41.8668 131.958 43.3668C147.946 31.9951 154.251 24.3799 155.949 6.3359C156.405 -0.457951 153.868 -1.18617 145.953 1.33172C126.443 7.18973 116.964 11.3406 96.9723 24.8513C92.474 27.8913 85.4013 42.6337 81.4784 55.3773C80.2921 48.2112 81.886 42.2835 87.9758 28.8552C55.8502 55.649 39.7982 70.6929 41.994 97.9128C39.5175 95.431 38.7454 90.9693 38.4954 77.3957C23.5133 99.6188 20.803 112.102 28.9991 134.443C23.1209 128.812 21.1453 123.764 19.0031 111.924C18.2376 136.396 21.0715 143.353 26.5001 153.459C24.2699 166.607 22.3286 173.933 18.0035 186.987C15.576 196.612 14.9375 200.04 15.0047 203C17.2779 201.094 20.1504 195.77 22.5017 186.486C24.5074 178.567 25.8177 167.63 29.499 154.96Z'

export const QuillIcon: React.FC<QuillIconProps> = ({
  size = 24,
  color = '#FDB549',
  animated = false,
}) => {
  const aspectRatio = 156 / 203
  const width = size * aspectRatio
  const height = size

  const svgPadding = 20
  const viewBoxWidth = 156 + svgPadding * 2
  const viewBoxHeight = 203 + svgPadding * 2
  const viewBox = `-${svgPadding} -${svgPadding} ${viewBoxWidth} ${viewBoxHeight}`

  return (
    <Container style={{ width, height }}>
      <svg width={width} height={height} viewBox={viewBox} fill="none">
        <QuillGroup $animated={animated}>
          <path d={QUILL_PATH} fill={color} />
        </QuillGroup>
        {sparkleData.map((sparkle, index) => (
          <SparkleGroup
            key={index}
            $animated={animated}
            $delay={index * 0.4}
            style={{
              transformOrigin: `${sparkle.originX}px ${sparkle.originY}px`,
            }}
          >
            <path d={sparkle.d} fill={color} />
          </SparkleGroup>
        ))}
      </svg>
    </Container>
  )
}

const Container = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'visible',
})

const QuillGroup = styled('g')<{ $animated: boolean }>(({ $animated }) => ({
  transformOrigin: 'center center',
  animation: $animated ? `${quillWriting} 1.8s ease-in-out infinite` : 'none',
}))

const SparkleGroup = styled('g')<{ $animated: boolean; $delay: number }>(
  ({ $animated, $delay }) => ({
    opacity: $animated ? 0 : 1,
    animation: $animated
      ? `${sparkleFade} 2.5s ease-in-out ${$delay}s infinite`
      : 'none',
  }),
)
